import React from 'react';
import { Box, Typography } from '@mui/material';
import { ChartCard } from '../ChartCard';
import { tokens } from '../tokens';
import type { ChartSpec, ChartData } from '../copilotTypes';

type KpiSpec = Extract<ChartSpec, { chart: 'kpi' }>;
type KpiData = Extract<ChartData, { chart: 'kpi' }>;

// Labels arrive snake_cased (e.g. "steps_total") — humanize to spaced words so the uppercase eyebrow
// reads as "STEPS TOTAL" instead of "STEPS_TOTAL".
function humanizeLabel(label: string) {
  return label.replace(/_/g, ' ');
}

// Values can be long decimals (e.g. 65.35087719298245) or large integers (e.g. 2499720). Keep big
// integers exact and grouped, but cap fractional precision so non-integers read cleanly (65.4),
// always via toLocaleString() for locale-aware grouping.
function formatValue(value: number) {
  const fractionDigits = Number.isInteger(value) ? 0 : 1;
  return value.toLocaleString(undefined, { maximumFractionDigits: fractionDigits });
}

export function KpiCards({ title, spec: _spec, data }: { title: string; spec: KpiSpec; data: KpiData }) {
  const { items } = data;

  return (
    <ChartCard title={title}>
      <Box
        sx={{
          display: 'grid',
          // auto-fit keeps a single readable row when it fits, then wraps gracefully for 1–6 tiles,
          // each tile keeping a comfortable minimum width before flowing to the next row.
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: 2,
        }}
      >
        {items.map((item) => (
          <Box
            key={item.key}
            sx={{
              bgcolor: tokens.surface.page,
              border: `1px solid ${tokens.surface.border}`,
              borderRadius: '12px',
              px: 2,
              py: 1.75,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.75,
            }}
          >
            <Typography
              sx={{
                fontFamily: tokens.fontFamily,
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: tokens.text.secondary,
                lineHeight: 1.3,
              }}
            >
              {humanizeLabel(item.label)}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap' }}>
              <Typography
                sx={{
                  fontFamily: tokens.fontFamily,
                  fontSize: 30,
                  fontWeight: 700,
                  lineHeight: 1.1,
                  color: tokens.text.primary,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {formatValue(item.value)}
              </Typography>
              {item.unit ? (
                <Typography
                  sx={{
                    fontFamily: tokens.fontFamily,
                    fontSize: 13,
                    fontWeight: 500,
                    color: tokens.text.secondary,
                  }}
                >
                  {item.unit}
                </Typography>
              ) : null}
            </Box>
          </Box>
        ))}
      </Box>
    </ChartCard>
  );
}
